import SFetch from '@app/utils/sfetch'
import type { DrawerState } from '@app/application/report/types'

// Mirrors `Report::GraphLogic::GRAPH_TYPES`. Keep in sync with
// app/models/concerns/report/graph_logic.rb when chart types are added.
export const GRAPH_TYPES = ['linechart', 'piechart', 'waterfallchart'] as const
export type GraphType = typeof GRAPH_TYPES[number]
export const DEFAULT_GRAPH_TYPE: GraphType = 'linechart'

// Defaults for everything beyond the user-provided title and formula. Keys
// track Ruby-side constants:
//   - graph_type / year_type → Report::GraphLogic
//   - series_type / value_type → Report::GraphValueLogic
//   - periods → Report::GraphPeriodLogic
const TIME_SERIES_DEFAULTS = {
  graphType: DEFAULT_GRAPH_TYPE,
  yearType: 'booking_year',
  seriesType: 'line',
  valueType: '',
  periods: 'all_periods_current_year',
} as const

export interface CreateMetricInput {
  title: string
  formula: string
  graphType?: GraphType
}

export async function setHighlightedRequest(params: {
  reportTemplateId: number
  graphId: number
  highlighted: boolean
}): Promise<DrawerState> {
  const url = window.Silverfin.Routes.highlight_report_report_graph_path({
    report_id: params.reportTemplateId,
    id: params.graphId,
    company_id: parseInt(window.Silverfin.currentCompanyId, 10),
    firm_id: parseInt(window.Silverfin.currentFirmId, 10),
    _options: true,
  })
  const response = await SFetch.json<DrawerState>(url, {
    method: 'PATCH',
    body: { highlighted: params.highlighted },
  })
  if (!response) {
    throw new Error('Empty drawer state response')
  }
  return response
}

export async function fetchDrawerStateRequest(params: {
  reportTemplateId: number
}): Promise<DrawerState> {
  const url = window.Silverfin.Routes.drawer_state_report_report_graphs_path({
    report_id: params.reportTemplateId,
    company_id: parseInt(window.Silverfin.currentCompanyId, 10),
    firm_id: parseInt(window.Silverfin.currentFirmId, 10),
    _options: true,
  })
  const response = await SFetch.json<DrawerState>(url)
  if (!response) {
    throw new Error('Empty drawer state response')
  }
  return response
}

export async function createMetricRequest(params: {
  reportTemplateId: number
  locale: string
  input: CreateMetricInput
}): Promise<void> {
  const url = window.Silverfin.Routes.report_report_graphs_path({
    report_id: params.reportTemplateId,
    company_id: parseInt(window.Silverfin.currentCompanyId, 10),
    firm_id: parseInt(window.Silverfin.currentFirmId, 10),
    _options: true,
  })
  const titleField = `title_${params.locale}`
  const nameField = `name_${params.locale}`

  // The model requires a complete graph (title + at least one valued series + a
  // periods row). We seed defaults for everything beyond the user-provided
  // title and formula so the modal stays minimal — see #31798. Users can refine
  // the chart in the standalone editor afterwards.
  const body = {
    report_graph: {
      [titleField]: params.input.title,
      graph_type: params.input.graphType ?? TIME_SERIES_DEFAULTS.graphType,
      year_type: TIME_SERIES_DEFAULTS.yearType,
      report_graph_values_attributes: [
        {
          formula: params.input.formula,
          [nameField]: params.input.title,
          series_type: TIME_SERIES_DEFAULTS.seriesType,
          value_type: TIME_SERIES_DEFAULTS.valueType,
          position: 0,
        },
      ],
      report_graph_periods_attributes: [
        {
          periods: TIME_SERIES_DEFAULTS.periods,
          delta: false,
          position: 0,
        },
      ],
    },
  }

  await SFetch.json(url, { method: 'POST', body })
}

export async function setPinnedRequest(params: {
  reportTemplateId: number
  graphId: number
  pinned: boolean
}): Promise<DrawerState> {
  const url = window.Silverfin.Routes.pin_report_report_graph_path({
    report_id: params.reportTemplateId,
    id: params.graphId,
    company_id: parseInt(window.Silverfin.currentCompanyId, 10),
    firm_id: parseInt(window.Silverfin.currentFirmId, 10),
    _options: true,
  })
  const response = await SFetch.json<DrawerState>(url, {
    method: 'PATCH',
    body: { pinned: params.pinned },
  })
  if (!response) {
    throw new Error('Empty drawer state response')
  }
  return response
}

export async function setPinToTopRequest(params: {
  reportTemplateId: number
  pinToTop: boolean
}): Promise<void> {
  const url = window.Silverfin.Routes.pin_to_top_report_path({
    id: params.reportTemplateId,
    company_id: parseInt(window.Silverfin.currentCompanyId, 10),
    firm_id: parseInt(window.Silverfin.currentFirmId, 10),
    _options: true,
  })
  await SFetch.json(url, {
    method: 'PATCH',
    body: { pin_to_top: params.pinToTop },
  })
}

export async function reorderHighlightedRequest(params: {
  reportTemplateId: number
  orderedIds: number[]
}): Promise<DrawerState> {
  const url =
    window.Silverfin.Routes.reorder_highlighted_report_report_graphs_path({
      report_id: params.reportTemplateId,
      company_id: parseInt(window.Silverfin.currentCompanyId, 10),
      firm_id: parseInt(window.Silverfin.currentFirmId, 10),
      _options: true,
    })
  const response = await SFetch.json<DrawerState>(url, {
    method: 'PATCH',
    body: { orderedIds: params.orderedIds },
  })
  if (!response) {
    throw new Error('Empty drawer state response')
  }
  return response
}

export async function reorderInsightRequest(params: {
  reportTemplateId: number
  orderedIds: number[]
}): Promise<DrawerState> {
  const url = window.Silverfin.Routes.reorder_insight_report_report_graphs_path(
    {
      report_id: params.reportTemplateId,
      company_id: parseInt(window.Silverfin.currentCompanyId, 10),
      firm_id: parseInt(window.Silverfin.currentFirmId, 10),
      _options: true,
    }
  )
  const response = await SFetch.json<DrawerState>(url, {
    method: 'PATCH',
    body: { orderedIds: params.orderedIds },
  })
  if (!response) {
    throw new Error('Empty drawer state response')
  }
  return response
}
